export type Brand = "dbg" | "dmns" | "felix-paul" | "lightwheel" | "luma" | "varjo" | "vive";
type LogoVariant = "wordmark" | "lockup" | "stack" | "symbol";
type LogoAssetPair = { onDark: string; onLight: string };

const normalizedRoleAssets: Partial<Record<Brand, string>> = {
  lightwheel: "/brands/optimized/role-lightwheel.webp",
  luma: "/brands/optimized/role-luma.webp",
  varjo: "/brands/optimized/role-varjo.webp",
  vive: "/brands/optimized/role-vive.webp",
};

const brandAssets: Record<Brand, Partial<Record<LogoVariant, LogoAssetPair>> & { lockup: LogoAssetPair }> = {
  dbg: {
    lockup: {
      onDark: "/brands/optimized/dbg-lockup.webp",
      onLight: "/brands/optimized/dbg-lockup.webp",
    },
  },
  dmns: {
    lockup: {
      onDark: "/brands/dmns/DMNS_Transparent_White.png",
      onLight: "/brands/dmns/DMNS_Transparent_White.png",
    },
  },
  "felix-paul": {
    lockup: {
      onDark: "/brands/felix-paul/FP_Logo_Black.png",
      onLight: "/brands/felix-paul/FP_Logo_Black.png",
    },
  },
  lightwheel: {
    lockup: {
      onDark: "/brands/lightwheel/Lightwheel_Lockup_Color_White.webp",
      onLight: "/brands/lightwheel/Lightwheel_Lockup_Color_White.webp",
    },
    symbol: {
      onDark: "/brands/lightwheel/Lightwheel_Symbol.png",
      onLight: "/brands/lightwheel/Lightwheel_Symbol.png",
    },
  },
  luma: {
    wordmark: {
      onDark: "/brands/luma/PNGs/Luma_White.png",
      onLight: "/brands/luma/PNGs/Luma_Black.png",
    },
    lockup: {
      onDark: "/brands/luma/PNGs/Luma_Lockup_White.png",
      onLight: "/brands/luma/PNGs/Luma_Lockup_Black.png",
    },
    stack: {
      onDark: "/brands/luma/PNGs/Luma_Stack_White.png",
      onLight: "/brands/luma/PNGs/Luma_Stack_Black.png",
    },
  },
  varjo: {
    lockup: {
      onDark: "/brands/varjo/Varjo_Lockup_Black.png",
      onLight: "/brands/varjo/Varjo_Lockup_Black.png",
    },
  },
  vive: {
    lockup: {
      onDark: "/brands/optimized/htc-vive.webp",
      onLight: "/brands/optimized/htc-vive.webp",
    },
    stack: {
      onDark: "/brands/vive/VIVE_Primary_White.png",
      onLight: "/brands/vive/VIVE_Primary_Black.png",
    },
  },
} as const;

type BrandLogoProps = {
  brand: Brand;
  decoding?: "async" | "auto" | "sync";
  loading?: "eager" | "lazy";
  normalizedRole?: boolean;
  variant?: LogoVariant;
};

export function BrandLogo({ brand, decoding, loading, normalizedRole = false, variant = "lockup" }: BrandLogoProps) {
  const normalizedAsset = normalizedRole ? normalizedRoleAssets[brand] : undefined;
  const asset = brandAssets[brand][variant] ?? brandAssets[brand].lockup;

  if (normalizedAsset) {
    return (
      <span className={`brand-logo brand-logo-${brand} brand-logo-normalized`} aria-hidden="true">
        {/* The role-heading derivatives share a 200px canvas height and 180px painted-height target. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img alt="" className="brand-logo-on-dark" decoding={decoding} loading={loading} src={normalizedAsset} />
      </span>
    );
  }

  if (brand === "vive" && variant === "lockup") {
    return (
      <span className="brand-logo brand-logo-vive brand-logo-lockup" aria-hidden="true">
        {/* Keep the official cyan mark, then layer it over a white lockup. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img alt="" className="vive-duotone-text" decoding={decoding} loading={loading} src={asset.onDark} />
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img alt="" className="vive-duotone-mark" decoding={decoding} loading={loading} src={asset.onDark} />
      </span>
    );
  }

  return (
    <span className={`brand-logo brand-logo-${brand} brand-logo-${variant}`} aria-hidden="true">
      {/* These local official marks bypass the optimizer to preserve transparent edges. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img alt="" className="brand-logo-on-dark" decoding={decoding} loading={loading} src={asset.onDark} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img alt="" className="brand-logo-on-light" decoding={decoding} loading={loading} src={asset.onLight} />
    </span>
  );
}
